import { wait } from "./";

/**
 * Return DOM elements position coordinates of a specific tag id.
 * @param elementsName The HTML tag id.
 */

export const getElementDimsById = (
  elementsName: string
): DOMRect | undefined => {
  const isServer = typeof window === "undefined";
  if (isServer) return undefined;

  const el: HTMLElement | null = document.getElementById(elementsName);
  if (el === null) return undefined;
  return el.getBoundingClientRect();
};

export const scrollToElementById = (id: string, delay = 0) => {
  const isServer = typeof window === "undefined";
  if (isServer) return undefined;

  const el = document.getElementById(id);
  if (el) {
    wait(delay).then(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }
};

export const getMobileDetect = (userAgent: NavigatorID["userAgent"]) => {
  const isAndroid = () => Boolean(userAgent.match(/Android/i));
  const isIos = () => Boolean(userAgent.match(/iPhone|iPad|iPod/i));
  const isOpera = () => Boolean(userAgent.match(/Opera Mini/i));
  const isWindows = () => Boolean(userAgent.match(/IEMobile/i));
  const isSSR = () => Boolean(userAgent.match(/SSR/i));

  const isMobile = () =>
    Boolean(isAndroid() || isIos() || isOpera() || isWindows());
  const isDesktop = () => Boolean(!isMobile() && !isSSR());

  return {
    isMobile: isMobile(),
    isDesktop: isDesktop(),
    isSSR: isSSR()
  };
};

// Get a new searchParams string by merging the current
// searchParams with a provided key/value pair
export const addSearchParam = (
  searchParams:
    | string
    | URLSearchParams
    | string[][]
    | Record<string, string>
    | undefined,
  name: string,
  values: string | string[]
) => {
  const params = new URLSearchParams(
    searchParams ? searchParams?.toString() : undefined
  );

  if (Array.isArray(values)) {
    values.forEach((value) => {
      params.set(name, value);
    });
  } else {
    params.set(name, values);
  }

  return params.toString();
};

// Get a new searchParams string by merging the current
// searchParams with a provided key/value pair
export const removeSearchParam = (
  searchParams:
    | string
    | URLSearchParams
    | string[][]
    | Record<string, string>
    | undefined,
  name: string
) => {
  // remove all filters
  if (name === "all") {
    const params = new URLSearchParams();
    const c = params.get("category");
    const s = params.get("s");
    if (c) params.set("category", c); // add categories filter
    if (s) params.set("s", s); // add search term
    return params.toString();
  }

  // remove a specific filter
  const params = new URLSearchParams(
    searchParams ? searchParams?.toString() : undefined
  );
  params.delete(encodeURI(name));
  return params.toString();
};

// Get a new searchParams string by merging the current
// searchParams with a provided key/value pair
export const replaceSearchParamValue = (
  searchParams:
    | string
    | URLSearchParams
    | string[][]
    | Record<string, string>
    | undefined,
  name: string,
  newValue: string
) => {
  const params = new URLSearchParams(
    searchParams ? searchParams?.toString() : undefined
  );

  // remove old value
  params.delete(encodeURI(name));

  // add new value
  params.set(name, newValue);

  return params.toString();
};
